import {
    Vector3,
    Quaternion,
    Bone,
    Mesh,
    BoxGeometry,
    MeshBasicMaterial,
    SphereGeometry,
    Color,
    MeshStandardMaterial,
    Group,
    Raycaster,
    Box3,
} from "three";
import { MathUtils } from "three";
import { AssetType, CollisionType, IRobotJoints, JointType } from "./enums";
import { ITransform, JointLimits } from "./interfaces";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils.js";
import { OBB } from "assets/JS/OBB";
import { Room } from "./Room";
import { checkBVHCollision, checkCollision, checkCollisionWalls } from "./Collision";
import { GraphicService } from "./GraphicService";
import { Inventory } from "./InventoryService";
import { builder } from "./global";

export class Robot {
    _arm: Bone[] = [];
    name = "";
    group: Group;
    type = AssetType.Robot;
    _jointsType: JointType[] = [];
    _effector: Bone; //virtual effector
    _jointsLimits: JointLimits[] = [];
    _robotLength = 0;
    _dim = 0;
    _root: Bone;
    size: { width: number; height: number; depth: number } = {
        width: 0,
        height: 0,
        depth: 0,
    };
    lightColor!: Color;
    lightColorName!: string;
    private _d: number[] = [];
    private _baseToWrist = 0.0;
    private _effectorToWrist = 0.0;
    _p: Vector3[] = []; // cached joints position
    private maxIter = 3000;
    private wp = 1.5;
    private woStart = 0.3;
    private wo = this.woStart;
    private elbowIndex = 4;
    private shoulderIndex = this.elbowIndex - 3;
    private saved_q: number[] = [];
    isLocked = false;
    isSleepMode = true;
    private isShoulderlocked = false;
    savedPose: number[] = [];
    savedRootPosition: Vector3 | undefined;
    wasSleepMode = false;
    ArmDecomposition: Mesh[] = [];
    ArmGroup: Group;
    //instrumentGroup:Group;
    base!: Mesh;
    private numOfSph = 3;
    private indices = [0, 2, 4, 6, 9];
    private zones = [IRobotJoints.base, IRobotJoints.Link, IRobotJoints.Link, IRobotJoints.Link];
    baseMesh!: Mesh;
    armMesh!: Mesh;
    index = 0;
    collisiongroup: Group;
    heightRef: number;
    maxHeight: number;
    maxRange = 0.33;
    targetPos: Vector3 | undefined;
    targetOr: Vector3 | undefined;
    bsuRotationReminder?: number;
    oldTarget: { position: Vector3 | undefined; orientation: Vector3 | undefined };

    /**
     * Define a robot arm
     * @param arm chain of joints defining the arm
     * @param effector the tip of the arm
     * @param jointsLimits define the range of motion of each joint
     */
    constructor(arm: Bone[], effector: Bone, root: Bone, jointsLimits: JointLimits[] = []) {
        this.group = new Group();
        this.collisiongroup = new Group();
        this.ArmGroup = new Group();
        this.group.name = "Object Group";
        this.collisiongroup.name = "Collision Group";
        this.ArmGroup.name = "ArmGroup";
        this._arm = arm;
        this._effector = effector;
        this._root = root;
        this.oldTarget = { position: undefined, orientation: undefined };
        if (this._arm) {
            this._arm[0].updateWorldMatrix(true, true);
            this._dim = this._arm.length;
            this.initDistances();
            this.buildCollisionPrimitives();
        }
        this.heightRef = this.getJointPos(0).y;
        this.maxHeight = this.heightRef + this.maxRange;
        for (let i = 0; i < this._dim - 1; i++) {
            this._jointsType.push(JointType.Pivot);
        }
        this._jointsType[1] = JointType.Hinge;
        this._jointsType[3] = JointType.Hinge;
        this._jointsType[5] = JointType.Hinge;
        this._jointsType[6] = JointType.Hinge;

        if (jointsLimits) this._jointsLimits = jointsLimits;
    }
    clone(): Robot {
        const nGroup = SkeletonUtils.clone(this.group);
        const baseName = this.baseMesh.name;
        const armName = this.armMesh.name;
        let nBaseMesh!: Mesh;
        let nArmMesh!: Mesh;
        const bones: Bone[] = [];
        let nRoot: Bone;
        const nArm: Bone[] = [];
        nGroup.traverse((_child: Mesh) => {
            if (_child instanceof Mesh) {
                _child.frustumCulled = false;
                _child.castShadow = false;
                //(_child.material as MeshStandardMaterial).wireframe = true;
                if (_child.name === baseName) nBaseMesh = _child;
                if (_child.name === armName) nArmMesh = _child;
            }
            if (_child instanceof Bone) {
                bones.push(_child);
                if (_child.name === "root") nRoot = _child;
            }
        });

        nRoot = bones[0];
        nArm.push(bones[3]);
        for (let i = 3; i < 12; i++) {
            nArm.push(bones[i]);
        }
        const nEffector = this._effector.clone();
        const n = nArm.length;

        nArm[2].userData["jointLimits"] = {
            qmin: (-2.0 * Math.PI) / 3.0,
            qmax: (2.0 * Math.PI) / 3.0,
        };
        nArm[4].userData["jointLimits"] = { qmin: 0.0, qmax: 0.95 * Math.PI };
        nArm[n - 4].userData["jointLimits"] = { qmin: 0, qmax: Math.PI };
        nArm[n - 3].userData["jointLimits"] = { qmin: -Math.PI, qmax: 0.0 };
        nArm[n - 1].add(nEffector);
        nEffector.matrixWorld = nArm[n - 1].matrixWorld.clone();

        this.index++;
        const name = this.name + " - " + this.index;
        const clonedArm = new Robot(nArm, nEffector, nRoot);
        clonedArm.group = nGroup;
        nBaseMesh.material = (nBaseMesh.material as MeshStandardMaterial).clone();
        nArmMesh.material = (nArmMesh.material as MeshStandardMaterial).clone();
        clonedArm.setBSUMesh(nBaseMesh, nArmMesh);
        clonedArm.name = name;

        return clonedArm;
    }
    private reset(): void {
        this._arm[1].rotation.y = 0.0;
        this._arm[1].updateMatrixWorld();
        for (let i = 2; i < this._dim - 1; i++) {
            const bone = this._arm[i];
            bone.rotation.x = 0.0;
            bone.updateMatrixWorld();
        }
        let link = this._dim - 3;
        this._arm[link].rotation.x = -Math.PI / 2.0;
        link = this._dim - 4;
        this._arm[link].rotation.x = Math.PI / 2.0;
        this._arm[this.elbowIndex].rotation.x = MathUtils.degToRad(175);
        this._arm[this.elbowIndex].updateMatrixWorld(true);
        this.updatePos();
    }
    private sleepMode(): void {
        this.reset();
        this._arm[this.elbowIndex].rotation.x = MathUtils.degToRad(170);
        this._arm[this.elbowIndex].updateMatrixWorld(true);
        this.updatePos();
    }
    private unlinkWithPort() {
        if (builder.room.bsuPortLink[this.lightColorName] != undefined) {
            const selectedPort = builder.room.bsuPortLink[this.lightColorName].portName;
            builder.room.trocarToColor[selectedPort] = "";
            builder.room.bsuPortLink[this.lightColorName].portName = "";
        }
    }
    setPose(poseName: string): void {
        switch (poseName) {
            case "standard":
                this.reset();
                this.isSleepMode = true;
                this.wasSleepMode = this.isSleepMode;
                this.unlinkWithPort();
                /* console.log(this.group);
                this.group.traverse((child) => {
                    console.log(child.name);
                    if (child.name.startsWith("instrument_fenestrated_grasper")) {
                        child.visible = false;
                    }
                }); */
                this.cameraInstrumentVisibility(false);

                break;
            case "sleep":
                this.sleepMode();
                break;
        }

        this.targetPos = undefined;
        this.targetOr = undefined;
        this.isLocked = false;
    }

    cameraInstrumentVisibility(state: boolean): void {
        this.group.traverse((_child) => {
            if (_child.name === "Mesh003_5" || _child.name === "instrument_fenestrated_grasper") {
                _child.visible = state;
            }
        });
    }

    lockEffector(): void {
        this.isLocked = true;
    }
    private initDistances(): void {
        this._d = [];
        this._p = [];
        this._robotLength = 0;
        this._p.push(this.getJointPos(0));

        for (let i = 0; i < this._dim - 1; i++) {
            const di = this.getDistance(this.getJointPos(i), this.getJointPos(i + 1));
            this._p.push(this.getJointPos(i + 1));
            this._d.push(di);
            this._robotLength += di;
            if (i == this.elbowIndex + 2) {
                this._baseToWrist = this._robotLength;
            }
        }
        /* console.log(this._d);
        console.log(this._baseToWrist); */
        //add a virtual effector
        const di = this.getDistance(this.getJointPos(this._dim - 2), this.getEffectorPos());
        this._p.push(this.getEffectorPos());
        this._d.push(di);
        this._robotLength += di;
        this._effectorToWrist = this._robotLength - this._baseToWrist;
    }

    updatePos(): void {
        this._p = [];
        this._p.push(this.getJointPos(0));

        for (let i = 0; i < this._dim - 1; i++) {
            this._p.push(this.getJointPos(i + 1));
        }
        this._p.push(this.getEffectorPos());

        let k = 0;

        for (let i = 1; i < this.indices.length; i++) {
            const di = this.getDistance(this._p[this.indices[i - 1]], this._p[this.indices[i]]);
            const dir = this.getDirection(this._p[this.indices[i - 1]], this._p[this.indices[i]]);

            const radius = di / this.numOfSph;
            for (let j = 0; j < this.numOfSph; j++) {
                const step = new Vector3().addScaledVector(dir, radius * j);
                step.add(this._p[this.indices[i - 1]]);
                this.ArmDecomposition[k].position.copy(step);
                this.ArmDecomposition[k].updateMatrixWorld(true);
                this.ArmDecomposition[k].geometry.computeBoundingSphere();
                /* this.ArmDecomposition[k].userData.sphere.copy( this.ArmDecomposition[k].geometry.boundingSphere);
                (this.ArmDecomposition[k].userData.sphere as Sphere).applyMatrix4(this.ArmDecomposition[k].matrixWorld); */
                k++;
            }
        }
        const tipDecomp = this.wristToTipPositions();
        //console.log("tipDecomp", tipDecomp);
        const n = this.ArmDecomposition.length;
        tipDecomp.forEach((sph) => {
            if (k < n) {
                this.ArmDecomposition[k].position.copy(sph.position.clone());
                this.ArmDecomposition[k].updateMatrixWorld(true);
                this.ArmDecomposition[k].geometry.computeBoundingSphere();
                k++;
            }
        });
    }
    getDimension(): { width: number; height: number; depth: number } {
        return this.size;
    }
    getRootPosition(): Vector3 {
        const rootPos = new Vector3();
        rootPos.setFromMatrixPosition(this._root.matrixWorld);
        return rootPos;
    }
    setRootPosition(p: Vector3, room: Room | undefined): boolean {
        const currentPosition = this._root.position.clone();
        //this.savePose();
        if (!this.isSleepMode)
            this.oldTarget = { position: this.targetPos, orientation: this.targetOr };
        this.wasSleepMode = this.isSleepMode;
        this.setBaseCollisionPosition(p);
        const bed = room?.SM_bed;
        let localPosition = p.clone();
        let bedRotation = 0;

        if (bed) {
            bed.group.updateWorldMatrix(true, true);

            const bedCenter = new Box3().setFromObject(bed.group).getCenter(new Vector3());
            const bedQuaternion = bed.group.getWorldQuaternion(new Quaternion());
            const bedForward = new Vector3(0, 0, 1).applyQuaternion(bedQuaternion);

            bedForward.y = 0;
            bedForward.normalize();

            bedRotation = MathUtils.radToDeg(Math.atan2(bedForward.x, bedForward.z));
            localPosition = p
                .clone()
                .sub(bedCenter)
                .applyQuaternion(bedQuaternion.clone().invert());
        }

        const isBesideBed = Math.abs(localPosition.x) * 2.5 >= Math.abs(localPosition.z);
        let alpha = isBesideBed
            ? localPosition.x >= 0
                ? 90
                : -90
            : localPosition.z >= 0
            ? 0
            : 180;

        alpha += bedRotation;
        const oldRot = this.getRotationY();
        alpha = this.bsuRotationReminder != undefined ? this.bsuRotationReminder : alpha;
        this.setRotationY(alpha, undefined);
        if (room) {
            let collision = CollisionType.NoCollision;
            collision = this.checkCollisionBSU(room);
            if (collision === CollisionType.NoCollision)
                collision = this.checkCollisionEquipment(room);
            if (collision === CollisionType.NoCollision) collision = checkCollisionWalls(this);
            //const collision = Math.max(eqCol, bsuCol, wallCol);
            if (collision > CollisionType.NoCollision) {
                this.setBaseCollisionPosition(currentPosition);
                const alphaRad = MathUtils.degToRad(oldRot);
                this._root.rotation.y = alphaRad;
                //this.setRotationY(oldRot, undefined);
                this.resetPose();
                return false;
            }
        }
        // console.log(this.name, p);
        this._root.position.copy(p);
        this._root.updateMatrixWorld(true);
        /* this.targetPos = undefined;
        this.targetOr = undefined;
        if (this.targetPos) {
            this.IKSolve(this.targetPos, this.targetOr);
            this.updatePos();
        } */

        return true;
        //this._root.updateMatrixWorld(true);
    }
    private setBaseCollisionPosition(pos: Vector3): void {
        this.base.position.x = pos.x;
        this.base.position.z = pos.z;
        this.collisiongroup.updateMatrixWorld(true);
        this.base.updateMatrix();
        this.base.updateMatrixWorld();
        const obb = this.base.userData.obb as OBB;
        obb.copy(this.base.geometry.userData.obb);
        obb.applyMatrix4(this.base.matrixWorld);
        this._root.position.copy(pos);
        this._root.updateMatrixWorld(true);
        this.updatePos();
    }
    private setBaseCollisionRotation(alpha: number): void {
        this.base.rotation.y = alpha;
        this.base.updateMatrix();
        this.base.updateMatrixWorld();
        const obb = this.base.userData.obb as OBB;
        obb.copy(this.base.geometry.userData.obb);
        obb.applyMatrix4(this.base.matrixWorld);
        this._root.rotation.y = alpha;
        this._root.updateMatrixWorld(true);
        this.updatePos();
    }
    setRootRotation(alpha: number, room: Room | undefined): boolean {
        const curremtAlpha = this._root.rotation.y;
        let hexColor = "#00FF00";
        this.setBaseCollisionRotation(alpha);
        if (room) {
            if (this.checkCollisionEquipment(room)) {
                this.setBaseCollisionRotation(curremtAlpha);
                return false;
            }
            if (this.checkCollisionBSU(room)) {
                hexColor = "#FF0000";
            }
            GraphicService.highlightObject(this, hexColor);
        }
        this._root.rotation.y = alpha;
        this._root.updateMatrixWorld(true);

        this.updatePos();
        return true;
    }
    getRotationY(): number {
        return MathUtils.radToDeg(this._root.rotation.y);
    }
    setRotationY(alpha: number, room: Room | undefined): void {
        const alphaRad = MathUtils.degToRad(alpha);
        this.setRootRotation(alphaRad, room);
        if (this.targetPos) {
            this.IKSolve(this.targetPos, this.targetOr);
        }
    }
    rotate(alpha: number): void {
        this._root.rotation.y += alpha;
        this._root.updateMatrixWorld(true);
        this.base.rotation.y += alpha;
        this.updatePos();
    }
    getEffectorPos(): Vector3 {
        const effectorPos = new Vector3();
        effectorPos.setFromMatrixPosition(this._effector.matrixWorld);
        return effectorPos;
    }

    getEffectorOr(): Vector3 {
        const h = new Vector3().subVectors(this.getEffectorPos(), this.getJointPos(this._dim - 1));
        h.normalize();

        return h;
    }

    getJointPos(link: number): Vector3 {
        const jointPos = new Vector3();
        this._arm[link].updateMatrixWorld();
        jointPos.setFromMatrixPosition(this._arm[link].matrixWorld);
        return jointPos;
    }

    private getDistance(p1: Vector3, p2: Vector3): number {
        const diff = new Vector3();
        diff.subVectors(p1, p2);
        return diff.length();
    }
    private getDirection(p1: Vector3, p2: Vector3): Vector3 {
        const diff = new Vector3();
        diff.subVectors(p2, p1);
        diff.normalize();
        return diff;
    }
    getOrErro(targetOrientation: Vector3): number {
        const h = this.getEffectorOr();
        const Or_err = Math.pow(h.dot(targetOrientation) - 1, 2);
        return Or_err;
    }
    getPosErr(target: Vector3): number {
        const effectorPos = this.getEffectorPos();
        return target.distanceTo(effectorPos);
    }
    private getError(
        target: Vector3,
        targetOrientation: Vector3 | undefined,
        effectorPos = this.getEffectorPos()
    ): number {
        const diffVect = new Vector3();
        diffVect.subVectors(target, effectorPos);
        let err = this.wp * diffVect.length();
        if (targetOrientation && this.wo > 0.0) {
            const h = this.getEffectorOr();
            const Or_err = Math.pow(h.dot(targetOrientation) - 1, 2);
            err += this.wo * Or_err;
        }
        return err;
    }

    /**
     * Solve inverse kinematic problem
     * @param target desired location of the tip of the arm
     * @param tol tolerated distance between the target and the effector
     */
    IKSolve(
        target: Vector3,
        targetOrientation: Vector3 | undefined,
        options?: { start?: number; end?: number; savePos?: boolean; tol?: number }
        /* onReady: (err: number) => void = () => {
            return;
        },
        onCompute: () => void = () => {
            return;
        } */
    ): void {
        // onCompute();
        const start = options?.start || -1;
        const end = options?.end || this._dim - 4;
        const savePos = options?.savePos == undefined ? true : options?.savePos && true;
        const tol = options?.tol || 1e-6;
        if (savePos) this.savePose();
        this.CCDSolver(target, targetOrientation, start, end, tol);
        //this.rotatePort();
        this.isShoulderlocked = false;
        //this.hightlight();
        const err = this.getError(target, targetOrientation);
        if (!this.isSleepMode) {
            if (this.checkCollisionBSU(builder.room) && savePos) {
                //if (err < 0.02) {}
                this.resetPose();
                // don't move the trocar.
                //this.rotatePort(true);
            } else {
                if (err < 0.02) {
                    this.rotatePort();
                }
            }
        }
    }
    rotatePort(force = false): void {
        if (!this.lightColorName || this.lightColorName === "") return;
        if (!builder.room.bsuPortLink[this.lightColorName]) return;
        if (builder.room.bsuPortLink[this.lightColorName].portName === "") return;
        const portName = builder.room.bsuPortLink[this.lightColorName].portName;
        const trocar = builder.room.SM_trocar[portName];

        if (!trocar) return;

        const q = this.getPortCorrectionTransform();
        if (!this.targetPos) return;
        const posErr = this.getPosErr(this.targetPos);

        if ((posErr > 0.004 && posErr < 0.025) || force) {
            const EffOr = this.getEffectorOr();
            const origin = this.getJointPos(this._dim - 1).clone();
            if (builder.room.SM_patient) {
                const intesects = new Raycaster(
                    origin,
                    EffOr,
                    0.0,
                    origin.distanceTo(this.getEffectorPos())
                ).intersectObject(builder.room.SM_patient.group, true);
                const n = intesects.length;
                if (n > 0) {
                    for (let i = n - 1; i > -1; i--) {
                        const intersect = intesects[i];
                        const normal = intersect.face?.normal
                            .clone()
                            .transformDirection(intersect.object.matrixWorld);
                        const distance = trocar.group
                            .getWorldPosition(new Vector3())
                            .distanceTo(intersect.point);
                        if (normal && EffOr.dot(normal) < 0 && distance < 0.03) {
                            trocar.setWorldPosition(intersect.point);
                            break;
                        }
                    }
                } else {
                    if (!this.isSleepMode) {
                        // console.log(posErr);
                        if (posErr < 0.011) trocar.setWorldPosition(this.getEffectorPos());
                    }
                }
            }
        }

        /**if (posErr < 0.02)
            builder.room.SM_trocar[portName].group.position.copy(this.getEffectorPos());*/
        if (q) {
            trocar.setWorldQuaternion(q);
            if (builder.room.SM_patient) {
                if (!trocar.isDepthValid(builder.room.SM_patient)) {
                    this.resetPose();
                }
            }
        }
    }

    getCurrentPose(): number[] {
        const pose: number[] = [];
        pose.push(this._arm[0].rotation.y);
        pose.push(this._arm[1].rotation.y);
        for (let i = 2; i < this._arm.length; i++) {
            pose.push(this._arm[i].rotation.x);
        }
        return pose;
    }

    getElbowPosition(): number[] {
        const elbowBone = this._arm[this.elbowIndex];
        elbowBone.updateMatrixWorld(true);
        const pos = new Vector3();
        pos.setFromMatrixPosition(elbowBone.matrixWorld);
        return pos.toArray();
    }

    private hightlight(): void {
        const collision = Math.max(
            this.checkCollisionEquipment(builder.room),
            this.checkCollisionBSU(builder.room)
        );
        let hexColor = "#00FF00";
        if (collision > CollisionType.NoCollision) {
            hexColor = "#FF0000";
            GraphicService.highlightObject(this, hexColor);
        }
        GraphicService.highlightObject(this, hexColor);
    }
    getPortCorrectionTransform(): Quaternion | undefined {
        if (!this.targetOr) return;
        if (!this.targetPos) return;

        const effOr = this.getEffectorOr();
        this.targetOr.normalize();
        const q = new Quaternion().setFromUnitVectors(new Vector3(0.0, -1.0, 0.0), effOr);
        q.normalize();
        return q;
    }

    private CCDSolver(
        target: Vector3,
        targetOrientation: Vector3 | undefined,
        start = -1,
        end = this._dim - 4,
        tol: number
    ): void {
        if (!this._arm) return;
        this.targetPos = new Vector3().copy(target);
        if (targetOrientation) this.targetOr = new Vector3().copy(targetOrientation);
        const j0 = this.getJointPos(0);
        const distanceToTarget = target.distanceTo(j0);
        if (distanceToTarget > this._robotLength * 0.85) {
            this.setPose("standard");
            return;
        }
        if (targetOrientation) {
            const wristTarget = this.findWristTarget(target, targetOrientation);
            const j0 = this.getJointPos(0);
            const distanceToWristTarget = wristTarget.distanceTo(j0);
            if (distanceToWristTarget > this._baseToWrist) {
                if (this.getPosErr(target) > 0.015 && !this.isSleepMode) {
                    this.setPose("standard");
                    return;
                }
                return;
            }
        } else {
            if (distanceToTarget > this._robotLength * 0.95) {
                this.setPose("standard");
                return;
            }
        }

        let threshHold = 1000;
        let iter = 0;
        let err = this.getError(target, targetOrientation);
        this.wo = this.woStart;
        err = this.getError(target, targetOrientation);

        while (err > tol && iter < this.maxIter) {
            for (let i = end; i > start; i--) {
                //const effectorPos = this.getEffectorPos();
                if (this.isShoulderlocked && i === this.shoulderIndex - 1) continue;
                const currentJoint = this._arm[i];
                let Fi = 0.0;
                if (i > 0) Fi = this.angleCorrection(currentJoint, target, targetOrientation);
                else
                    Fi = this.angleCorrection(
                        currentJoint,
                        target,
                        targetOrientation,
                        new Vector3(0.0, -1.0, 0.0)
                    );
                if (Number.isNaN(Fi)) {
                    continue;
                }
                if (Math.abs(Fi) < 1e-3) continue;
                if (this._arm[i + 1]?.userData?.jointLimits) {
                    let currentAngle = this._arm[i + 1].rotation.x;
                    currentAngle += Fi;
                    this._arm[i + 1].rotation.x = this.isIn(
                        currentAngle,
                        this._arm[i + 1].userData.jointLimits
                    );
                } else {
                    if (i > 0) this._arm[i + 1].rotation.x += Fi;
                    else this._arm[i + 1].rotation.y += Fi;
                }
                this._arm[i + 1].updateMatrixWorld(true);
            }

            err = this.getError(target, targetOrientation);
            //if (this.getPosErr(target) > 0.002);
            iter++;
            if (iter > threshHold) {
                this.wo *= 0.85;
                threshHold += 30;
            }
        }
        if (this.getPosErr(target) < 0.02) {
            this.updatePos();
            this.isSleepMode = false;
            this.cameraInstrumentVisibility(true);
        }
        //this.rotatePort();
    }

    private findWristTarget(target: Vector3, targetOrientation: Vector3) {
        const wristTarget = new Vector3()
            .copy(targetOrientation)
            .multiplyScalar(-this._effectorToWrist);
        wristTarget.add(target);
        return wristTarget;
    }
    private angleCorrection(
        currentJoint: Bone,
        target: Vector3,
        targetOrientation: Vector3 | undefined,
        zi = new Vector3(1.0, 0.0, 0.0)
    ): number {
        const effectorPos = this.getEffectorPos();
        const effectorVec = new Vector3();
        const targetVec = new Vector3();

        const jointPos = new Vector3();
        const jointQ = new Quaternion();
        const jointScale = new Vector3();
        const axis = new Vector3();

        currentJoint.matrixWorld.decompose(jointPos, jointQ, jointScale);

        effectorVec.subVectors(effectorPos, jointPos);

        targetVec.subVectors(target, jointPos);

        zi.applyQuaternion(jointQ);

        axis.crossVectors(effectorVec, targetVec);

        let k1 = this.wp * targetVec.dot(zi) * effectorVec.dot(zi);

        let k2 = this.wp * targetVec.dot(effectorVec);

        let k3 = this.wp * zi.dot(axis);
        if (targetOrientation && this.wo > 0.0) {
            const h = this.getEffectorOr();
            const axisOr = new Vector3().crossVectors(h, targetOrientation);
            k1 += this.wo * targetOrientation.dot(zi) * h.dot(zi);
            k2 += this.wo * targetOrientation.dot(h);
            k3 += this.wo * zi.dot(axisOr);
        }
        const angle1 = Math.atan(k3 / (k2 - k1));
        const angle2 = angle1 + Math.PI;
        const V1 = (k1 - k2) * Math.cos(angle1) - k3 * Math.sin(angle1);
        //const V2=(k1-k2)*Math.cos(angle2)-k3*Math.sin(angle2);

        const Fi = V1 < 0 ? angle1 : angle2;
        if (Number.isNaN(Fi)) return 0.0;
        return Fi;
    }

    ikElbow(targetElbow: Vector3): void {
        let iter = 0;
        let err = targetElbow.distanceTo(this.getJointPos(this.elbowIndex));
        while (err > 0.01 && iter < 20) {
            for (let i = 1; i > -1; i--) {
                const currentJoint = this._arm[i];
                let Fi = 0.0;
                if (i > 0) Fi = this.angleCorrectionElbow(currentJoint, targetElbow);
                else
                    Fi = this.angleCorrectionElbow(
                        currentJoint,
                        targetElbow,
                        new Vector3(0.0, -1.0, 0.0)
                    );
                if (Math.abs(Fi) < 1e-3) continue;
                if (this._arm[i + 1]?.userData?.jointLimits) {
                    let currentAngle = this._arm[i + 1].rotation.x;
                    currentAngle += Fi;
                    this._arm[i + 1].rotation.x = this.isIn(
                        currentAngle,
                        this._arm[i + 1].userData.jointLimits
                    );
                } else {
                    if (i > 0) this._arm[i + 1].rotation.x += Fi;
                    else this._arm[i + 1].rotation.y += Fi;
                }
                this._arm[i + 1].updateMatrixWorld(true);
            }
            err = targetElbow.distanceTo(this.getJointPos(this.elbowIndex));
            iter++;
        }
    }

    private angleCorrectionElbow(
        currentJoint: Bone,
        targetElbow: Vector3,
        zi = new Vector3(1.0, 0.0, 0.0)
    ): number {
        const effectorPos = this.getJointPos(this.elbowIndex);
        const effectorVec = new Vector3();
        const targetVec = new Vector3();

        const jointPos = new Vector3();
        const jointQ = new Quaternion();
        const jointScale = new Vector3();
        const axis = new Vector3();

        currentJoint.matrixWorld.decompose(jointPos, jointQ, jointScale);

        effectorVec.subVectors(effectorPos, jointPos);

        targetVec.subVectors(targetElbow, jointPos);

        zi.applyQuaternion(jointQ);

        axis.crossVectors(effectorVec, targetVec);

        const k1 = this.wp * targetVec.dot(zi) * effectorVec.dot(zi);

        const k2 = this.wp * targetVec.dot(effectorVec);

        const k3 = this.wp * zi.dot(axis);

        const angle1 = Math.atan(k3 / (k2 - k1));
        const angle2 = angle1 + Math.PI;
        const V1 = (k1 - k2) * Math.cos(angle1) - k3 * Math.sin(angle1);
        const Fi = V1 < 0 ? angle1 : angle2;

        return Fi;
    }
    moveElbow(targetElbow: Vector3): void {
        if (!this.targetPos) return;
        // console.log(this.isLocked);
        this.savePose();
        this.ikElbow(targetElbow);
        this.isShoulderlocked = true;
        this.isLocked = true;
        this.IKSolve(this.targetPos, this.targetOr, {
            start: 1,
            end: this._dim - 5,
            savePos: false,
        });

        if (
            this.getError(this.targetPos, undefined) > 0.02 ||
            (this.checkCollisionBSU(builder.room) &&
                this.getError(this.targetPos, undefined) < 0.02)
        ) {
            this.resetPose();
            this.updatePos();
            this.rotatePort(true);
            return;
        }
        //this.rotatePort();
        this.updatePos();
    }

    savePose(): void {
        this.savedPose = [];
        this.wasSleepMode = this.isSleepMode;
        this.oldTarget = { position: this.targetPos, orientation: this.targetOr };
        this.savedRootPosition = this._root.position.clone();
        this.savedPose.push(this._arm[0].rotation.y);
        this.savedPose.push(this._arm[1].rotation.y);
        for (let i = 2; i < this._arm.length; i++) {
            this.savedPose.push(this._arm[i].rotation.x);
        }
    }
    resetPose(pose?: number[]): void {
        if (pose) {
            this.savedPose = pose;
            this.isSleepMode = false;
        } else {
            if (this.wasSleepMode) {
                return this.setPose("standard");
            }
            if (this.savedRootPosition) {
                this._root.position.copy(this.savedRootPosition);
                //this._root.updateMatrixWorld(true);
                this.savedRootPosition = undefined;
            }
        }

        this._arm[0].rotation.y = this.savedPose[0];
        this._arm[1].rotation.y = this.savedPose[1];
        for (let i = 2; i < this._arm.length; i++) {
            this._arm[i].rotation.x = this.savedPose[i];
        }
        //this._arm[0].updateMatrixWorld(true);
        this._root.updateMatrixWorld(true);
        this.updatePos();
        this.targetPos = this.oldTarget.position ?? this.getEffectorPos();
        this.targetOr = this.oldTarget.orientation ?? this.getEffectorOr();
    }
    isIn(angle: number, jointLimits = { qmin: -Math.PI, qmax: Math.PI }): number {
        angle = this.normAng(angle);
        let qmin = jointLimits.qmin;
        let qmax = jointLimits.qmax;
        qmin = this.normAng(qmin);
        qmax = this.normAng(qmax);
        if (qmin >= qmax) {
            if (angle < qmax) angle += 2 * Math.PI;
            qmax += 2 * Math.PI;
        }

        if (angle >= qmin && angle <= qmax) return this.normAng(angle);
        else {
            angle = this.normAng(angle);
            qmin = this.normAng(qmin);
            qmax = this.normAng(qmax);
            const dmax = Math.min(Math.abs(angle - qmax), Math.abs(angle - qmax + 2 * Math.PI));
            const dmin = Math.min(Math.abs(angle - qmin), Math.abs(angle - qmin + 2 * Math.PI));

            return dmin < dmax ? this.normAng(qmin) : this.normAng(qmax);
        }
    }

    setBSUMesh(baseMesh: Mesh, armMesh: Mesh): void {
        this.baseMesh = baseMesh;
        this.armMesh = armMesh;
    }

    private normAng(a: number): number {
        return (a + 2 * Math.PI) % (2 * Math.PI);
    }

    setLight(color: { name: string; hexColor: string }, intensity = 2.5): void {
        if (this.lightColorName) {
            if (Inventory.colors[this.lightColorName]) {
                Inventory.colors[this.lightColorName].isUsed = false;
                if (builder.room.bsuPortLink[this.lightColorName]) {
                    builder.room.bsuPortLink[this.lightColorName].bsuName = "";
                }
            }
        }
        this.lightColor = new Color(color.hexColor);
        this.lightColorName = color.name;
        if (!builder.room.bsuPortLink[this.lightColorName])
            builder.room.bsuPortLink[this.lightColorName] = { bsuName: this.name, portName: "" };
        else {
            builder.room.bsuPortLink[this.lightColorName].bsuName = this.name;
            const transform = builder.room.getPortTransform(this.lightColorName);
            if (transform) {
                this.IKSolve(transform.target, transform.targetOrientation, {
                    start: -1,
                    end: this._dim - 4,
                });
            }
        }
        Inventory.colors[this.lightColorName].isUsed = true;
        (this.baseMesh.material as MeshStandardMaterial).emissive = new Color("#00FF00");
        (this.armMesh.material as MeshStandardMaterial).emissive = this.lightColor;
        (this.baseMesh.material as MeshStandardMaterial).emissiveIntensity = intensity;
        (this.armMesh.material as MeshStandardMaterial).emissiveIntensity = intensity;
    }

    setArmHeight(p = 0): void {
        const pourcentage = Math.min(Math.max(p, 0), 100);
        let newHeight = (pourcentage / 100) * this.maxRange;
        newHeight += this.heightRef;
        const delta = newHeight - this.getJointPos(0).y;
        if (delta + this.getJointPos(0).y < this.maxHeight) {
            this._arm[0].parent?.translateY(delta);
            this._arm[0].parent?.updateMatrixWorld(true);
        }
        if (this.targetPos) {
            this.IKSolve(this.targetPos, this.targetOr, { start: -1, end: this._dim - 4 });
        } else {
            this.updatePos();
        }
    }
    getArmHeight(): number {
        const h = this.getJointPos(0).y - this.heightRef;
        const p = (h / this.maxRange) * 100;
        return p;
    }
    getTransform(): ITransform {
        const transform: ITransform = { position: [], rotation: 0 };
        this._root.position.toArray(transform.position);
        transform.rotation = MathUtils.radToDeg(this._root.rotation.y);
        return transform;
    }
    getPosition(): Vector3 {
        const pos = new Vector3().copy(this.getRootPosition());
        pos.y = 0.0;
        return pos;
    }
    dispose(): void {
        this._root.clear();
        this.group.clear();
        this.group.children.forEach((obj) => {
            if (obj instanceof Mesh) {
                obj.geometry.dispose();
                obj.material.dispose();
            }
        });
        this.collisiongroup.children.forEach((obj) => {
            if (obj instanceof Mesh) {
                obj.geometry.dispose();
                obj.material.dispose();
            }
        });
    }
    private checkCollisionEquipment(room: Room): CollisionType {
        if (room.SM_stack) {
            if (checkCollision(this, room.SM_stack) > CollisionType.NoCollision) {
                return CollisionType.Collision;
            }
        }
        if (room.SM_console) {
            if (checkCollision(this, room.SM_console) > CollisionType.NoCollision) {
                return CollisionType.Collision;
            }
        }

        if (room.SM_trolley) {
            if (checkCollision(this, room.SM_trolley) > CollisionType.NoCollision) {
                return CollisionType.Collision;
            }
        }

        if (room.SM_anesthesia) {
            if (checkCollision(this, room.SM_anesthesia) > CollisionType.NoCollision) {
                return CollisionType.Collision;
            }
        }

        if (room.SM_surgeon) {
            if (checkCollision(this, room.SM_surgeon) > CollisionType.NoCollision) {
                return CollisionType.Collision;
            }
        }

        if (room.SM_slave) {
            if (checkCollision(this, room.SM_slave) > CollisionType.NoCollision) {
                return CollisionType.Collision;
            }
        }

        return CollisionType.NoCollision;
    }
    private checkCollisionBSU(room: Room): CollisionType {
        let result = CollisionType.NoCollision;
        if (room.SM_vbsu) {
            for (const key in room.SM_vbsu) {
                if (key !== this.name) {
                    result = Math.max(checkCollision(this, room.SM_vbsu[key]), result);
                    if (result === CollisionType.Collision) {
                        return result;
                    }
                }
            }
        }
        if (room.SM_ibsu) {
            for (const key in room.SM_ibsu) {
                if (key !== this.name)
                    result = Math.max(checkCollision(this, room.SM_ibsu[key]), result);
                if (result === CollisionType.Collision) {
                    return result;
                }
            }
        }
        if (room.SM_bed) {
            //result = Math.max(checkCollision(this, room.SM_bed), result);
            /* if (checkCollision(this, room.SM_bed) > CollisionType.NoCollision)
                return CollisionType.Collision; */
            if (checkBVHCollision(this, room.SM_bed) > CollisionType.NoCollision) {
                return CollisionType.Collision;
            }
        }
        if (room.SM_patient) {
            //result = Math.max(checkCollision(this, room.SM_patient), result);
            /* if (checkCollision(this, room.SM_patient) > CollisionType.NoCollision)
                return CollisionType.Collision; */
            // console.log(checkBVHCollision(this, room.SM_patient));
            if (checkBVHCollision(this, room.SM_patient) > CollisionType.NoCollision) {
                return CollisionType.Collision;
            }
        }
        return result;
    }
    private buildCollisionPrimitives(): void {
        // base box

        const size = this.getJointPos(1);
        size.x = size.z = 0.4;
        const center = this.getRootPosition();
        center.y += 0.15;
        //this.base = new Box3().setFromCenterAndSize(center, size);
        const mat = new MeshBasicMaterial({ visible: false, toneMapped: false });
        this.base = new Mesh(new BoxGeometry(size.x, size.y, size.z), mat);
        this.size = { width: size.x, depth: size.z, height: size.y };
        this.base.position.copy(center);
        this.base.updateMatrix();
        this.base.geometry.computeBoundingBox();
        this.base.geometry.userData["obb"] = new OBB();
        (this.base.geometry.userData.obb as OBB).halfSize.copy(size).multiplyScalar(0.5);
        this.base.name = "Base";
        this.base.userData = {
            type: AssetType.Robot,
            bsu: this,
            obb: new OBB(),
            joint: IRobotJoints.base,
        };
        this.collisiongroup.add(this.base);
        this.collisiongroup.add(this.ArmGroup);
        for (let i = 1; i < this.indices.length; i++) {
            const di = this.getDistance(this._p[this.indices[i - 1]], this._p[this.indices[i]]);
            const dir = this.getDirection(this._p[this.indices[i - 1]], this._p[this.indices[i]]);
            const joint = this.zones[i - 1];
            const radius = di / this.numOfSph;
            const geom = new SphereGeometry(radius / 2, 8, 8);
            for (let j = 0; j < this.numOfSph; j++) {
                const step = new Vector3().addScaledVector(dir, radius * j);
                step.add(this._p[this.indices[i - 1]]);
                const mesh = new Mesh(geom, mat);
                mesh.frustumCulled = false;
                mesh.position.copy(step);
                mesh.geometry.computeBoundingSphere();
                mesh.name = "Arm - " + j;
                mesh.userData = { type: AssetType.Robot, bsu: this };
                mesh.userData["joint"] = joint;
                //this.collisiongroup.add(mesh);
                this.ArmGroup.add(mesh);
                this.ArmDecomposition.push(mesh);
            }
        }

        const tipDecomp = this.wristToTipPositions();
        //console.log("tipDecomp ", tipDecomp);
        let c = 0;
        tipDecomp.forEach((sph) => {
            const geom = new SphereGeometry(sph.radius / 2, 8, 8);
            const mesh = new Mesh(geom, mat);
            mesh.frustumCulled = false;
            mesh.position.copy(sph.position);
            mesh.geometry.computeBoundingSphere();
            mesh.name = "Arm - " + this.ArmDecomposition.length;
            mesh.userData = { type: AssetType.Robot, bsu: this };
            mesh.userData["joint"] = c < 7 ? IRobotJoints.Link : IRobotJoints.tip;
            this.ArmGroup.add(mesh);
            this.ArmDecomposition.push(mesh);
            c++;
        });

        this.ArmDecomposition[5].userData["joint"] = IRobotJoints.Elbow;
        this.ArmDecomposition[6].userData["joint"] = IRobotJoints.Elbow;
        this.ArmDecomposition[7].userData["joint"] = IRobotJoints.Elbow;
        this.ArmDecomposition[9].userData["joint"] = IRobotJoints.Wrist;
        this.ArmDecomposition[10].userData["joint"] = IRobotJoints.Wrist;
        this.ArmDecomposition[11].userData["joint"] = IRobotJoints.Wrist;
    }
    private wristToTipPositions(): { radius: number; position: Vector3 }[] {
        const result: { radius: number; position: Vector3 }[] = [];
        const dir = this.getEffectorOr();

        const p0 = this.getJointPos(this._dim - 3);
        let radius = 0.07;
        const pos = new Vector3().addScaledVector(dir, radius * 3).add(p0);
        result.push({ radius, position: pos.clone() });

        const dist = this._effectorToWrist - radius * 3;
        const step = dist / 5.5;
        const extentionLength = 6;
        for (let i = 0; i < extentionLength; i++) {
            radius = step / 2;
            pos.add(new Vector3().addScaledVector(dir, radius));
            result.push({ radius, position: pos.clone() });
        }

        return result;
    }

    public deactivateForBedMove(): void {
        // Return the arm to its inactive pose without unlinking the trocar.
        this.reset();

        this.targetPos = undefined;
        this.targetOr = undefined;

        this.isLocked = false;
        this.isSleepMode = true;
        this.wasSleepMode = true;

        this.cameraInstrumentVisibility(false);

        this._root.updateWorldMatrix(true, true);
        this.updatePos();
    }
}
